import { useEffect, useRef } from 'react';
import {
  diffPresenceTransitions,
  buildPresenceNotifications
} from '../utils/dashboard/memberTreeHelpers';
import { PRESENCE_STATUS } from '../utils/dashboard/constants';

const NOTIFICATION_HOLD_MS = 5000;
/** Same member+type cooldown to avoid spam */
const NOTIFY_COOLDOWN_MS = 10 * 60 * 1000;

/**
 * Presence notifications:
 * - No toast for temp_offline (orange only)
 * - Online toast only on offline(gray) -> online(green); orange<->green silent
 * - Per-member per-type cooldown 10 min
 * - Online: expand org/group + highlight only; do NOT auto-expand member detail (token/coords)
 */
export default function useOnlineMemberNotifications({
  members,
  presenceById,
  onlineBaselineReadyRef,
  lastOnlineMembers,
  setLastOnlineMembers,
  setOpenOrg,
  setOpenGroup,
  setOpenMember,
  setHighlightedMemberIds,
  setOnlineNotifications,
  onPresenceTick
}) {
  const membersRef = useRef(members);
  const presenceMapRef = useRef(new Map());
  const lastNotifyAtRef = useRef(new Map());
  // setOpenMember retained for callers; online path no longer auto-expands detail
  void setOpenMember;

  useEffect(() => { membersRef.current = members; }, [members]);

  useEffect(() => {
    if (lastOnlineMembers instanceof Set) {
      /* keep for Dashboard state shape */
    }
  }, [lastOnlineMembers]);

  useEffect(() => {
    const list = membersRef.current;
    const currentMap = new Map();
    Object.entries(presenceById || {}).forEach(([id, status]) => {
      currentMap.set(id, status);
    });

    if (!list || list.length === 0 || currentMap.size === 0) {
      onPresenceTick?.();
      return;
    }

    if (!onlineBaselineReadyRef.current) {
      onlineBaselineReadyRef.current = true;
      presenceMapRef.current = currentMap;
      const onlineIds = new Set();
      currentMap.forEach((status, id) => {
        if (status === PRESENCE_STATUS.ONLINE || status === PRESENCE_STATUS.TEMP_OFFLINE) {
          onlineIds.add(id);
        }
      });
      setLastOnlineMembers(onlineIds);
      onPresenceTick?.();
      return;
    }

    const { toOnline, toOffline } = diffPresenceTransitions(
      currentMap,
      presenceMapRef.current
    );

    const realOnline = new Set();
    toOnline.forEach((id) => {
      const before = presenceMapRef.current.get(id);
      if (before === PRESENCE_STATUS.OFFLINE || before == null) realOnline.add(id);
    });

    const pushNotifications = (ids, type) => {
      if (!ids.size) return;
      const now = Date.now();
      const cooled = new Set();
      ids.forEach((id) => {
        const key = type + ':' + id;
        const last = lastNotifyAtRef.current.get(key) || 0;
        if (now - last < NOTIFY_COOLDOWN_MS) return;
        lastNotifyAtRef.current.set(key, now);
        cooled.add(id);
      });
      if (!cooled.size) return;

      const {
        notifications: newNotifications,
        orgToOpen,
        groupToOpen,
        memberToOpen
      } = buildPresenceNotifications(list, cooled, type);

      if (type === 'online') {
        // 只展开组织/小组树，不高亮展开队员详情（令牌/经纬度等）
        setOpenOrg((prev) => ({ ...prev, ...orgToOpen }));
        setOpenGroup((prev) => ({ ...prev, ...groupToOpen }));
        setHighlightedMemberIds((prev) => {
          const next = new Set(prev);
          Object.keys(memberToOpen).forEach((key) => next.add(key));
          return next;
        });
      }

      setOnlineNotifications((prev) => [...prev, ...newNotifications].slice(-8));
      setTimeout(() => {
        setOnlineNotifications((prev) => prev.filter((n) => !newNotifications.find((nn) => nn.id === n.id)));
      }, NOTIFICATION_HOLD_MS);
    };

    pushNotifications(realOnline, 'online');
    pushNotifications(toOffline, 'offline');

    presenceMapRef.current = currentMap;
    const onlineIds = new Set();
    currentMap.forEach((status, id) => {
      if (status === PRESENCE_STATUS.ONLINE || status === PRESENCE_STATUS.TEMP_OFFLINE) {
        onlineIds.add(id);
      }
    });
    setLastOnlineMembers(onlineIds);
    onPresenceTick?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presenceById, members]);
}
